import { useLingui } from '@lingui/react/macro';
import { Ellipsis, Pencil, Plus, Trash2, type LucideIcon } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import type { z } from 'zod';

import { notifyApiError, useNotifier } from '@/shared/data';
import { useIsMobile } from '@/shared/mobile';
import { EmptyState } from '@/shared/page';
import { ColumnValue, resolvePatternText, type ColumnSpec } from '@/shared/patterns';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/shared/ui/dropdown-menu';
import { toast } from '@/shared/ui/toaster';

import { useConfirm } from './confirm-dialog';
import { EntitySheet, useSheetState } from './entity-sheet';
import { useBound } from './fields';

/** Textos da coleção; o nome do item entra onde a frase pede (U39 §6: o nome do registro no diálogo). */
export interface CollectionTexts<Item> {
  readonly title?: ReactNode;
  /** "3 envolvidos". */
  readonly count?: (count: number) => string;
  /** "Adicionar envolvido". */
  readonly add: string;
  readonly emptyTitle: string;
  readonly emptyDescription?: string;
  readonly sheetAddTitle: string;
  readonly sheetEditTitle: string;
  readonly sheetDescription?: string;
  /** Botão do sheet no adicionar ("Adicionar envolvido") e no editar ("Salvar envolvido"). */
  readonly submitAdd: string;
  readonly submitEdit: string;
  /** Nome do item nos rótulos dos botões ("Editar Juliana Prado Menezes"). */
  readonly itemName: (item: Item) => string;
  readonly removeTitle: (item: Item) => string;
  readonly removeDescription?: (item: Item) => string;
  /** "Sim, remover" (U20-6). */
  readonly removeConfirm: string;
  /** Toast depois de adicionar ou editar (da tela, X06). Ausente: sem toast. */
  readonly saved?: string;
  readonly removed?: string;
  /** Mensagem de item repetido (mesma chave). */
  readonly duplicate?: string;
}

export interface CollectionRowAction<Item> {
  readonly id: string;
  readonly label: (item: Item) => string;
  readonly icon: LucideIcon;
  readonly onSelect: (item: Item, index: number) => void;
  /** Aparece também em somente leitura (o "abrir cadastro da pessoa" do legado). */
  readonly always?: boolean;
}

export interface CollectionFieldProps<V extends FieldValues, Item extends FieldValues> {
  /** O array no formulário pai (`relatedPersonFund`, `relatedDocs`): só vai ao backend no Salvar do pai. */
  readonly name: Path<V>;
  readonly texts: CollectionTexts<Item>;
  /** Colunas por preset (`columnsFor<Item>()`): a 1ª de texto é o título do cartão no iPhone. */
  readonly columns: readonly ColumnSpec[];
  /** Identidade estável do item (o legado usava `personId`; repetir quebrava a grade). */
  readonly getKey: (item: Item) => string;
  readonly schema: z.ZodType<Item, Item>;
  readonly empty: Item;
  readonly renderForm: (form: UseFormReturn<Item>, context: { readonly mode: 'add' | 'edit'; readonly item: Item | undefined }) => ReactNode;
  /** Nome do sheet na URL (`?sheet=envolvido`). */
  readonly sheet: string;
  readonly mobileSheet?: 'screen' | 'sheet';
  /** Ajusta o item antes de gravar (rótulos `*Display`, campos derivados). */
  readonly toItem?: (values: Item, previous: Item | undefined) => Item;
  /** Campo onde a mensagem de repetido aparece. */
  readonly duplicateField?: Path<Item>;
  /** Persistência opcional antes de retirar o item; falha mantém a lista. */
  readonly onRemove?: (item: Item) => Promise<void>;
  readonly rowActions?: readonly CollectionRowAction<Item>[];
  /** Salvar do sheet só quando `true` (upload em andamento, por exemplo). */
  readonly canSubmit?: (values: Item) => boolean;
  readonly readOnly?: boolean;
  readonly className?: string;
}

/**
 * Lista editável dentro do formulário pai (P12): adicionar e editar num `EntitySheet`, remover com
 * confirmação (U20-3). Nada vai ao servidor aqui; o array segue no Salvar do pai. Tabela na web,
 * cartões no iPhone.
 */
export function CollectionField<V extends FieldValues, Item extends FieldValues>(props: CollectionFieldProps<V, Item>) {
  const { texts, columns, getKey, schema, empty, renderForm, sheet, mobileSheet, toItem, duplicateField, rowActions = [], canSubmit, className } = props;
  const { t } = useLingui();
  const isMobile = useIsMobile();
  const notifier = useNotifier();
  const headingId = useId();
  const bound = useBound<V>({ name: props.name, ...(props.readOnly ? { readOnly: true } : {}) });
  const readOnly = bound.readOnly;
  const items: Item[] = Array.isArray(bound.field.value) ? (bound.field.value as Item[]) : [];
  const state = useSheetState<{ readonly index: number }>(sheet);
  const [confirm, confirmElement] = useConfirm();

  const editing = state.context ? items[state.context.index] : undefined;
  const mode: 'add' | 'edit' = editing ? 'edit' : 'add';
  const write = (next: Item[]) => {
    bound.field.onChange(next);
    bound.field.onBlur();
  };

  const remove = async (item: Item, index: number) => {
    const ok = await confirm({
      title: texts.removeTitle(item),
      description: texts.removeDescription?.(item) ?? t`O item sai da lista. A mudança só vale quando você salvar o cadastro.`,
      confirmLabel: texts.removeConfirm,
    });
    if (!ok) return;
    try {
      if (props.onRemove) await props.onRemove(item);
    } catch (error) {
      notifyApiError(notifier, error);
      return;
    }
    write(items.filter((_, position) => position !== index));
    if (texts.removed) toast.success(texts.removed);
  };

  const submit = (values: Item, form: UseFormReturn<Item>) => {
    const item = toItem ? toItem(values, editing) : values;
    const key = getKey(item);
    const clash = items.some((other, position) => getKey(other) === key && position !== state.context?.index);
    if (clash && duplicateField) {
      form.setError(duplicateField, { type: 'duplicate', message: texts.duplicate ?? t`Esse item já está na lista.` });
      return false;
    }
    if (editing && state.context) write(items.map((other, position) => (position === state.context?.index ? item : other)));
    else write([...items, item]);
    if (texts.saved) toast.success(texts.saved);
    return true;
  };

  const actionsFor = (item: Item, index: number) => [
    ...rowActions.filter((action) => action.always === true || !readOnly).map((action) => ({ id: action.id, label: action.label(item), icon: action.icon, run: () => action.onSelect(item, index), danger: false })),
    ...(readOnly
      ? []
      : [
          { id: 'edit', label: t`Editar`, icon: Pencil, run: () => state.show({ index }), danger: false },
          { id: 'remove', label: t`Remover`, icon: Trash2, run: () => void remove(item, index), danger: true },
        ]),
  ];
  const itemName = texts.itemName;
  const titleColumn = columns.find((column) => column.preset === 'text') ?? columns[0];
  const metaColumns = columns.filter((column) => column !== titleColumn);

  const addButton = readOnly ? null : (
    <Button variant="secondary" onClick={() => state.show()}>
      <Plus aria-hidden />
      {texts.add}
    </Button>
  );

  const list =
    items.length === 0 ? (
      <EmptyState
        title={texts.emptyTitle}
        {...(texts.emptyDescription ? { description: texts.emptyDescription } : {})}
        compact={isMobile}
        {...(readOnly ? {} : { action: <Button onClick={() => state.show()}>{texts.add}</Button> })}
      />
    ) : isMobile ? (
      <ul aria-labelledby={headingId} className="flex flex-col gap-3">
        {items.map((item, index) => (
          <li key={getKey(item)} className="flex flex-col gap-2 rounded-card border border-border bg-card p-4">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1 text-base font-semibold text-foreground">{titleColumn ? <ColumnValue column={titleColumn} row={item} /> : getKey(item)}</div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label={t`Ações de ${itemName(item)}`}>
                    <Ellipsis aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {actionsFor(item, index).map((action) => (
                    <DropdownMenuItem key={action.id} tone={action.danger ? 'danger' : 'default'} onSelect={action.run}>
                      <action.icon aria-hidden />
                      {action.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <dl className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {metaColumns.map((column) => (
                <div key={column.field} className="flex gap-1">
                  <dt className="sr-only">
                    <ColumnHeader column={column} />
                  </dt>
                  <dd className="text-foreground">
                    <ColumnValue column={column} row={item} />
                  </dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    ) : (
      <div className="overflow-x-auto rounded-card border border-border bg-card">
        <table aria-labelledby={headingId} className="w-full text-sm">
          <thead className="border-b border-structural text-left text-xs font-medium text-muted-foreground">
            <tr>
              {columns.map((column) => (
                <th key={column.field} scope="col" className="px-4 py-2.5 font-medium">
                  <ColumnHeader column={column} />
                </th>
              ))}
              <th scope="col" className="w-px px-4 py-2.5">
                <span className="sr-only">{t`Ações`}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={getKey(item)} className="border-b border-border last:border-b-0">
                {columns.map((column, position) => (
                  <td key={column.field} className={cn('px-4 py-2.5 align-middle', position === 0 && 'font-medium text-foreground')}>
                    <ColumnValue column={column} row={item} />
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  {actionsFor(item, index).map((action) => (
                    <Button
                      key={action.id}
                      variant="ghost"
                      size="icon"
                      className={cn('size-control-sm', action.danger && 'text-err')}
                      aria-label={`${action.label} ${itemName(item)}`}
                      title={action.label}
                      onClick={action.run}
                    >
                      <action.icon aria-hidden />
                    </Button>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

  return (
    <section data-slot="collection-field" aria-labelledby={headingId} className={cn('flex min-w-0 flex-col gap-3', className)}>
      <header className="flex flex-wrap items-center gap-3">
        <h2 id={headingId} className="text-base font-semibold text-foreground md:text-lg">
          {texts.title ?? texts.add}
        </h2>
        {texts.count && items.length > 0 ? <span className="text-sm text-muted-foreground">{texts.count(items.length)}</span> : null}
        {items.length > 0 && addButton ? <div className="ml-auto">{addButton}</div> : null}
      </header>
      {bound.error ? (
        <p role="alert" className="text-sm text-err">
          {bound.error}
        </p>
      ) : null}
      {list}
      <EntitySheet<Item>
        key={`${mode}:${String(state.context?.index ?? 'new')}`}
        open={state.open}
        onOpenChange={state.setOpen}
        title={mode === 'edit' ? texts.sheetEditTitle : texts.sheetAddTitle}
        {...(texts.sheetDescription ? { description: texts.sheetDescription } : {})}
        schema={schema}
        defaultValues={editing ?? empty}
        submitLabel={mode === 'edit' ? texts.submitEdit : texts.submitAdd}
        onSubmit={submit}
        readOnly={readOnly}
        {...(mobileSheet ? { mobile: mobileSheet } : {})}
        {...(canSubmit ? { canSubmit } : {})}
      >
        {(form) => renderForm(form, { mode, item: editing })}
      </EntitySheet>
      {confirmElement}
    </section>
  );
}

function ColumnHeader({ column }: { readonly column: ColumnSpec }) {
  const { i18n } = useLingui();
  return <>{resolvePatternText(i18n, column.label)}</>;
}
